import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { 
  ClipboardList, 
  Search, 
  Filter,
  RefreshCw,
  Eye,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  MoreHorizontal,
  Edit,
  CheckCircle,
  XCircle,
  Package
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminService } from "@/services/adminService";
import { productService } from "@/services/productService";
import SudanesePound from "@/components/shared/sudanese-pound";
import { Skeleton } from "@/components/ui/skeleton";

interface OrderItem {
  id: string;
  product_id?: string;
  quantity: number;
  price: string;
  subtotal: string;
  product?: {
    id: string;
    name: string;
    name_ar?: string;
    thumbnail_url?: string;
    sku?: string;
  };
}

interface Order {
  id: string;
  user: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  total_amount: string;
  payment_method: string;
  status: string;
  receipt_status: string;
  receipt_url?: string;
  address: string;
  phone: string;
  items_count: number;
  items_summary: OrderItem[];
  created_at: string;
  updated_at: string;
}

export default function Orders() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [receiptStatus, setReceiptStatus] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFormData, setStatusFormData] = useState({
    status: '',
    remarks: ''
  });
  const [rejectFormData, setRejectFormData] = useState({
    remarks: ''
  });

  // Build filters object for API
  const filters = useMemo(() => {
    const filterObj: any = {
      page: currentPage,
      limit: limit
    };
    if (status) filterObj.status = status;
    if (paymentMethod) filterObj.payment_method = paymentMethod;
    if (receiptStatus) filterObj.receipt_status = receiptStatus;
    if (startDate) filterObj.start_date = startDate;
    if (endDate) filterObj.end_date = endDate;
    return filterObj;
  }, [currentPage, limit, status, paymentMethod, receiptStatus, startDate, endDate]);

  // React Query for orders data
  const { data: ordersResponse, isLoading: ordersLoading, error: ordersError, refetch: refetchOrders } = useQuery({
    queryKey: ['orders', filters],
    queryFn: () => adminService.getAllOrders(filters),
    staleTime: 30000, // 30 seconds
  });

  // React Query for order details
  const { data: orderDetailsResponse, isLoading: orderDetailsLoading, refetch: refetchOrderDetails } = useQuery<{ success: boolean; data: any; message?: string }>({
    queryKey: ['orderDetails', selectedOrder?.id],
    queryFn: async () => {
      if (!selectedOrder) {
        return { success: false, data: null, message: '' };
      }
      return adminService.getOrderById(selectedOrder.id);
    },
    enabled: !!selectedOrder && isViewDialogOpen,
  });

  // Use detailed order data if available, otherwise use selected order
  const displayOrder = useMemo(() => {
    if (orderDetailsResponse?.success && orderDetailsResponse?.data) {
      return orderDetailsResponse.data;
    }
    return selectedOrder;
  }, [orderDetailsResponse, selectedOrder]);

  // Mutations
  const updateStatusMutation = useMutation({
    mutationFn: ({ orderId, data }: { orderId: string; data: { status: string; remarks?: string } }) =>
      adminService.updateOrderStatus(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setIsStatusDialogOpen(false);
      setStatusFormData({ status: '', remarks: '' });
      toast({
        title: t('success') || 'Success',
        description: t('orderStatusUpdated') || 'Order status updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: t('error') || 'Error',
        description: error?.message || t('failedToUpdateOrderStatus') || 'Failed to update order status',
        variant: 'destructive',
      });
    },
  });

  const approveReceiptMutation = useMutation({
    mutationFn: (orderId: string) => adminService.approveReceipt(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (selectedOrder) {
        refetchOrderDetails();
      }
      toast({
        title: t('success') || 'Success',
        description: t('receiptApproved') || 'Receipt approved successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: t('error') || 'Error',
        description: error?.message || t('failedToApproveReceipt') || 'Failed to approve receipt',
        variant: 'destructive',
      });
    },
  });

  const rejectReceiptMutation = useMutation({
    mutationFn: ({ orderId, data }: { orderId: string; data: { remarks: string } }) =>
      adminService.rejectReceipt(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setIsRejectDialogOpen(false);
      setRejectFormData({ remarks: '' });
      if (selectedOrder) {
        refetchOrderDetails();
      }
      toast({
        title: t('success') || 'Success',
        description: t('receiptRejected') || 'Receipt rejected successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: t('error') || 'Error',
        description: error?.message || t('failedToRejectReceipt') || 'Failed to reject receipt',
        variant: 'destructive',
      });
    },
  });

  // Extract orders and pagination from response
  const orders = useMemo(() => {
    return ordersResponse?.success && ordersResponse?.data ? ordersResponse.data.orders : [];
  }, [ordersResponse]);

  const pagination = useMemo(() => {
    return ordersResponse?.success && ordersResponse?.data ? ordersResponse.data.pagination : {
      total: 0,
      page: 1,
      limit: 20,
      totalPages: 1
    };
  }, [ordersResponse]);

  // Calculate stats from orders
  const stats = useMemo(() => {
    if (orders.length > 0) {
      return {
        total: pagination.total,
        pending_payment: orders.filter(o => o.status === 'pending_payment').length,
        paid: orders.filter(o => o.status === 'paid').length,
        processing: orders.filter(o => o.status === 'processing').length,
        completed: orders.filter(o => o.status === 'completed').length,
        cancelled: orders.filter(o => o.status === 'cancelled').length,
      };
    }
    return { total: 0, pending_payment: 0, paid: 0, processing: 0, completed: 0, cancelled: 0 };
  }, [orders, pagination]);

  // Status display helpers
  const getStatusDisplay = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      'pending_payment': { label: t('pendingPayment') || 'Pending Payment', color: 'bg-yellow-100 text-yellow-800' },
      'reviewing_payment': { label: t('reviewingPayment') || 'Reviewing Payment', color: 'bg-blue-100 text-blue-800' },
      'paid': { label: t('paid') || 'Paid', color: 'bg-green-100 text-green-800' },
      'processing': { label: t('processing') || 'Processing', color: 'bg-purple-100 text-purple-800' },
      'shipped': { label: t('shipped') || 'Shipped', color: 'bg-indigo-100 text-indigo-800' },
      'out_for_delivery': { label: t('outForDelivery') || 'Out for Delivery', color: 'bg-cyan-100 text-cyan-800' },
      'completed': { label: t('completed') || 'Completed', color: 'bg-green-100 text-green-800' },
      'cancelled': { label: t('cancelled') || 'Cancelled', color: 'bg-red-100 text-red-800' },
    };
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  const getPaymentMethodDisplay = (method: string) => {
    const methodMap: Record<string, string> = {
      'bank_transfer': t('bankTransfer') || 'Bank Transfer',
      'cod': t('cashOnDelivery') || 'Cash on Delivery',
      'online': t('online') || 'Online',
    };
    return methodMap[method] || method;
  };

  const getReceiptStatusDisplay = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      'pending': { label: t('pending') || 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      'approved': { label: t('approved') || 'Approved', color: 'bg-green-100 text-green-800' },
      'rejected': { label: t('rejected') || 'Rejected', color: 'bg-red-100 text-red-800' },
    };
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  // Event handlers
  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setIsViewDialogOpen(true);
  };

  const handleUpdateStatus = (order: Order) => {
    setSelectedOrder(order);
    setStatusFormData({ status: order.status, remarks: '' });
    setIsStatusDialogOpen(true);
  };

  const handleApproveReceipt = (order: Order) => {
    if (confirm(t('confirmApproveReceipt') || 'Are you sure you want to approve this receipt?')) {
      approveReceiptMutation.mutate(order.id);
    }
  };

  const handleRejectReceipt = (order: Order) => {
    setSelectedOrder(order);
    setRejectFormData({ remarks: '' });
    setIsRejectDialogOpen(true);
  };

  const handleSaveStatus = () => {
    if (!selectedOrder || !statusFormData.status) {
      toast({
        title: t('validationError') || 'Validation Error',
        description: t('pleaseSelectStatus') || 'Please select a status',
        variant: 'destructive',
      });
      return;
    }
    updateStatusMutation.mutate({
      orderId: selectedOrder.id,
      data: {
        status: statusFormData.status,
        remarks: statusFormData.remarks || undefined,
      },
    });
  };

  const handleSaveReject = () => {
    if (!selectedOrder || !rejectFormData.remarks.trim()) {
      toast({
        title: t('validationError') || 'Validation Error',
        description: t('pleaseEnterRemarks') || 'Please enter rejection remarks',
        variant: 'destructive',
      });
      return;
    }
    rejectReceiptMutation.mutate({
      orderId: selectedOrder.id,
      data: { remarks: rejectFormData.remarks },
    });
  };

  const handleResetFilters = () => {
    setStatus('');
    setPaymentMethod('');
    setReceiptStatus('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const isLoading = ordersLoading;

  return (
    <div className="space-y-6" dir={t('language') === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('orders') || 'Orders Manage'}</h1>
          <p className="text-muted-foreground mt-1">
            {t('manageOrders') || 'Manage customer orders'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {t('filters') || 'Filters'}
          </Button>
          <Button
            variant="outline"
            onClick={() => refetchOrders()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {t('refresh') || 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('totalOrders') || 'Total Orders'}</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('pendingPayment') || 'Pending Payment'}</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending_payment}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('paid') || 'Paid'}</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.paid}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('processing') || 'Processing'}</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.processing}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('completed') || 'Completed'}</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('cancelled') || 'Cancelled'}</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.cancelled}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle>{t('filters') || 'Filters'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
              <div className="space-y-2">
                <Label>{t('status') || 'Status'}</Label>
                <Select value={status || 'all'} onValueChange={(value) => setStatus(value === 'all' ? '' : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('allStatuses') || 'All Statuses'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allStatuses') || 'All Statuses'}</SelectItem>
                    <SelectItem value="pending_payment">{t('pendingPayment') || 'Pending Payment'}</SelectItem>
                    <SelectItem value="reviewing_payment">{t('reviewingPayment') || 'Reviewing Payment'}</SelectItem>
                    <SelectItem value="paid">{t('paid') || 'Paid'}</SelectItem>
                    <SelectItem value="processing">{t('processing') || 'Processing'}</SelectItem>
                    <SelectItem value="shipped">{t('shipped') || 'Shipped'}</SelectItem>
                    <SelectItem value="out_for_delivery">{t('outForDelivery') || 'Out for Delivery'}</SelectItem>
                    <SelectItem value="completed">{t('completed') || 'Completed'}</SelectItem>
                    <SelectItem value="cancelled">{t('cancelled') || 'Cancelled'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('paymentMethod') || 'Payment Method'}</Label>
                <Select value={paymentMethod || 'all'} onValueChange={(value) => setPaymentMethod(value === 'all' ? '' : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('allMethods') || 'All Methods'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allMethods') || 'All Methods'}</SelectItem>
                    <SelectItem value="bank_transfer">{t('bankTransfer') || 'Bank Transfer'}</SelectItem>
                    <SelectItem value="cod">{t('cashOnDelivery') || 'Cash on Delivery'}</SelectItem>
                    <SelectItem value="online">{t('online') || 'Online'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('receiptStatus') || 'Receipt Status'}</Label>
                <Select value={receiptStatus || 'all'} onValueChange={(value) => setReceiptStatus(value === 'all' ? '' : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('allStatuses') || 'All Statuses'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allStatuses') || 'All Statuses'}</SelectItem>
                    <SelectItem value="pending">{t('pending') || 'Pending'}</SelectItem>
                    <SelectItem value="approved">{t('approved') || 'Approved'}</SelectItem>
                    <SelectItem value="rejected">{t('rejected') || 'Rejected'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('startDate') || 'Start Date'}</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('endDate') || 'End Date'}</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="space-y-2 flex items-end">
                <Button variant="outline" onClick={handleResetFilters} className="w-full">
                  {t('reset') || 'Reset'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('orders') || 'Orders'} ({isLoading ? '...' : pagination.total})</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={limit.toString()} onValueChange={(value) => {
                setLimit(Number(value));
                setCurrentPage(1);
              }}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
              <TableHead className="text-right">{t('actions') || 'Actions'}</TableHead>
                
                
                <TableHead>{t('totalAmount') || 'Total Amount'}</TableHead>
                <TableHead>{t('paymentMethod') || 'Payment Method'}</TableHead>
                <TableHead>{t('status') || 'Status'}</TableHead>
                <TableHead>{t('receiptStatus') || 'Receipt Status'}</TableHead>
                <TableHead>{t('date') || 'Date'}</TableHead>
                <TableHead>{t('customer') || 'Customer'}</TableHead>
                <TableHead>{t('orderId') || 'Order ID'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      {t('loading') || 'Loading'}...
                    </div>
                  </TableCell>
                </TableRow>
              ) : ordersError ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="text-muted-foreground">
                      {t('errorLoadingOrders') || 'Error loading orders'}
                    </div>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="text-muted-foreground">
                      {t('noOrdersFound') || 'No orders found'}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewOrder(order)}
                          className="h-8 w-8 p-0"
                          title={t('view') || 'View Order'}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">{t('openMenu') || 'Open menu'}</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>{t('actions') || 'Actions'}</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => handleViewOrder(order)}>
                              <Eye className="mr-2 h-4 w-4" />
                              {t('view') || 'View'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleUpdateStatus(order)}>
                              <Edit className="mr-2 h-4 w-4" />
                              {t('updateStatus') || 'Update Status'}
                            </DropdownMenuItem>
                            {order.receipt_status === 'pending' && order.receipt_url && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleApproveReceipt(order)}>
                                  <CheckCircle className="mr-2 h-4 w-4" />
                                  {t('approveReceipt') || 'Approve Receipt'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleRejectReceipt(order)}>
                                  <XCircle className="mr-2 h-4 w-4" />
                                  {t('rejectReceipt') || 'Reject Receipt'}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                    
                    
                    <TableCell>
                      <SudanesePound amount={parseFloat(order.total_amount)} variant="new" />
                    </TableCell>
                    <TableCell>{getPaymentMethodDisplay(order.payment_method)}</TableCell>
                    <TableCell>
                      <Badge className={getStatusDisplay(order.status).color}>
                        {getStatusDisplay(order.status).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getReceiptStatusDisplay(order.receipt_status).color}>
                        {getReceiptStatusDisplay(order.receipt_status).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {formatDate(order.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{order.user.name}</div>
                        <div className="text-sm text-muted-foreground">{order.user.email}</div>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{order.id.substring(0, 8)}...</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {pagination.totalPages > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-4">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                {t('showing') || 'Showing'} {((currentPage - 1) * limit) + 1}–{Math.min(currentPage * limit, pagination.total)} {t('of') || 'of'} {pagination.total} {t('orders') || 'orders'}
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || isLoading}
                >
                  {t('previous') || 'Prev'}
                </Button>

                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={isLoading}
                        className="w-9"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <span className="sm:hidden text-sm px-2 text-muted-foreground">
                  {currentPage} / {pagination.totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage === pagination.totalPages || isLoading}
                >
                  {t('next') || 'Next'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('orderDetails') || 'Order Details'}</DialogTitle>
            <DialogDescription>
              {t('viewOrderInformation') || 'View detailed order information'}
            </DialogDescription>
          </DialogHeader>
          
          {orderDetailsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : displayOrder ? (
            <div className="space-y-4">
              {/* Order Info */}
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">{t('orderId') || 'Order ID'}</Label>
                  <p className="font-medium text-xs break-all">{displayOrder.order_id || displayOrder.id}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('status') || 'Status'}</Label>
                  <div>
                    <Badge className={getStatusDisplay(displayOrder.status).color}>
                      {getStatusDisplay(displayOrder.status).label}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('totalAmount') || 'Total Amount'}</Label>
                  <p className="font-medium text-lg">
                    <SudanesePound amount={parseFloat(displayOrder.total_amount)} variant="new" />
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('paymentMethod') || 'Payment Method'}</Label>
                  <p className="font-medium">{getPaymentMethodDisplay(displayOrder.payment_method)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('receiptStatus') || 'Receipt Status'}</Label>
                  <div>
                    <Badge className={getReceiptStatusDisplay(displayOrder.receipt_status).color}>
                      {getReceiptStatusDisplay(displayOrder.receipt_status).label}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('itemsCount') || 'Items Count'}</Label>
                  <p className="font-medium">{displayOrder.items_count ?? (displayOrder.items || displayOrder.items_summary || []).length}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('createdAt') || 'Created At'}</Label>
                  <p className="font-medium">{formatDate(displayOrder.created_at)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('updatedAt') || 'Updated At'}</Label>
                  <p className="font-medium">{formatDate(displayOrder.updated_at)}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">{t('customerInformation') || 'Customer Information'}</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label className="text-muted-foreground">{t('name') || 'Name'}</Label>
                    <p className="font-medium">{displayOrder.user?.name || ''}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">{t('email') || 'Email'}</Label>
                    <p className="font-medium">{displayOrder.user?.email || ''}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">{t('phone') || 'Phone'}</Label>
                    <p className="font-medium">{displayOrder.user?.phone || ''}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">{t('address') || 'Address'}</Label>
                    <p className="font-medium">{displayOrder.address}</p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">
                  {t('orderItems') || 'Order Items'}
                  {(() => {
                    const items = displayOrder.items || displayOrder.items_summary || [];
                    return items.length > 0 ? ` (${items.length})` : '';
                  })()}
                </h3>
                {(() => {
                  const items: OrderItem[] = displayOrder.items || displayOrder.items_summary || [];
                  return items.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('product') || 'Product'}</TableHead>
                          <TableHead>{t('quantity') || 'Qty'}</TableHead>
                          <TableHead>{t('price') || 'Unit Price'}</TableHead>
                          <TableHead>{t('subtotal') || 'Subtotal'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item: OrderItem) => {
                          const productName = item.product?.name || item.product?.name_ar || `${t('product') || 'Product'} #${item.id?.substring(0, 8)}`;
                          return (
                            <TableRow key={item.id}>
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  {item.product?.thumbnail_url && (
                                    <img
                                      src={item.product.thumbnail_url}
                                      alt={productName}
                                      className="h-10 w-10 rounded object-cover flex-shrink-0"
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  )}
                                  <div>
                                    <div className="font-medium">{productName}</div>
                                    {item.product?.sku && (
                                      <div className="text-xs text-muted-foreground">SKU: {item.product.sku}</div>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">{item.quantity}</TableCell>
                              <TableCell>
                                <SudanesePound amount={parseFloat(item.price)} variant="new" />
                              </TableCell>
                              <TableCell>
                                <SudanesePound amount={parseFloat(item.subtotal)} variant="new" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground text-sm">{t('noItems') || 'No items found'}</p>
                  );
                })()}
              </div>

              {/* Receipt */}
              {displayOrder.receipt_url && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">{t('receipt') || 'Receipt'}</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <a
                        href={displayOrder.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t('viewReceipt') || 'View Receipt'}
                      </a>
                    </div>
                    {displayOrder.receipt_status === 'pending' && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setIsViewDialogOpen(false);
                            handleApproveReceipt(displayOrder);
                          }}
                          disabled={approveReceiptMutation.isPending}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {t('approveReceipt') || 'Approve'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setIsViewDialogOpen(false);
                            handleRejectReceipt(displayOrder);
                          }}
                          disabled={rejectReceiptMutation.isPending}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          {t('rejectReceipt') || 'Reject'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">{t('actions') || 'Actions'}</h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsViewDialogOpen(false);
                      handleUpdateStatus(displayOrder);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {t('updateStatus') || 'Update Status'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {t('noOrderData') || 'No order data available'}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('updateOrderStatus') || 'Update Order Status'}</DialogTitle>
            <DialogDescription>
              {t('updateOrderStatusDescription') || 'Update the status of this order'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('status') || 'Status'} *</Label>
              <Select 
                value={statusFormData.status} 
                onValueChange={(value) => setStatusFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('selectStatus') || 'Select Status'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_payment">{t('pendingPayment') || 'Pending Payment'}</SelectItem>
                  <SelectItem value="reviewing_payment">{t('reviewingPayment') || 'Reviewing Payment'}</SelectItem>
                  <SelectItem value="paid">{t('paid') || 'Paid'}</SelectItem>
                  <SelectItem value="processing">{t('processing') || 'Processing'}</SelectItem>
                  <SelectItem value="shipped">{t('shipped') || 'Shipped'}</SelectItem>
                  <SelectItem value="out_for_delivery">{t('outForDelivery') || 'Out for Delivery'}</SelectItem>
                  <SelectItem value="completed">{t('completed') || 'Completed'}</SelectItem>
                  <SelectItem value="cancelled">{t('cancelled') || 'Cancelled'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('remarks') || 'Remarks'} ({t('optional') || 'Optional'})</Label>
              <Textarea
                value={statusFormData.remarks}
                onChange={(e) => setStatusFormData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder={t('enterRemarks') || 'Enter remarks (optional)'}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)}>
              {t('cancel') || 'Cancel'}
            </Button>
            <Button 
              onClick={handleSaveStatus}
              disabled={updateStatusMutation.isPending}
            >
              {updateStatusMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t('saving') || 'Saving...'}
                </>
              ) : (
                <>
                  <Edit className="mr-2 h-4 w-4" />
                  {t('updateStatus') || 'Update Status'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Receipt Dialog */}
      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('rejectReceipt') || 'Reject Receipt'}</DialogTitle>
            <DialogDescription>
              {t('rejectReceiptDescription') || 'Provide a reason for rejecting this receipt'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('remarks') || 'Remarks'} *</Label>
              <Textarea
                value={rejectFormData.remarks}
                onChange={(e) => setRejectFormData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder={t('enterRejectionReason') || 'Enter rejection reason...'}
                rows={4}
                required
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRejectDialogOpen(false)}>
              {t('cancel') || 'Cancel'}
            </Button>
            <Button 
              onClick={handleSaveReject}
              disabled={rejectReceiptMutation.isPending}
              variant="destructive"
            >
              {rejectReceiptMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t('rejecting') || 'Rejecting...'}
                </>
              ) : (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  {t('rejectReceipt') || 'Reject Receipt'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
